import { Answer } from '@doltech/ui/lib/figma/DesignSystemV2/components';
import { DragOutMobile, DragOutMobileResult } from '@doltech/ui/lib/figma/DesignSystemV2/DragOutMobile';
import { MarkedTypeEnum, TestModeEnum } from '@doltech/utils/lib/constants';
import { isNumber } from '@doltech/utils/lib/number';
import { numberToABC } from '@doltech/utils/lib/text';
import cl from 'classnames';
import { observer } from 'mobx-react-lite';
import * as React from 'react';
import styled from 'styled-components';

import { ResultDragOutQuestionInstance } from '../../model/Question/result-drag-out-question.store';
import { useTestResultStore } from '../../model/test-result.hook';
import { ExtraQuestionProps } from './BaseQuestion';
import { Img, Main, Results, Spot } from './DragInQuestionResult';

interface DragOutResultProps extends React.HTMLAttributes<HTMLDivElement> {
  data: ResultDragOutQuestionInstance & ExtraQuestionProps;
}

export const getDragOutResult = ({
  store,
  questions,
  marked,
  correctOptions,
  userAnswers,
  no,
  index,
  spots,
  scroll,
}) => {
  let defaultMarkedType = null;
  if (store.testMode === TestModeEnum.EXPLANATION) {
    defaultMarkedType = MarkedTypeEnum.CORRECT;
  }

  return questions.map((question, idx) => {
    const markedType = defaultMarkedType || marked[idx];
    const optionIdx = correctOptions[idx].key;
    const userAnswerKey = userAnswers[idx];
    const number = no + idx - index;

    return {
      id: spots[idx].id,
      number,
      userAnswer: isNumber(userAnswerKey) ? numberToABC(userAnswerKey + 1) : undefined,
      correctText: question,
      correctAnswer: numberToABC(optionIdx + 1),
      markedType,
      isWatching: index === idx,
      goHere: () => {
        store.goToQuestion(number, scroll);
      },
    };
  });
};

export const DragOutQuestionResult = observer((props: DragOutResultProps) => {
  const { data, ...rest } = props;
  const { spots, image, marked, correctOptions, no, questions, index, userAnswers, scroll } = data;
  const store = useTestResultStore();

  const results = getDragOutResult({
    store,
    questions,
    marked,
    correctOptions,
    userAnswers,
    no,
    index,
    spots,
    scroll,
  });

  return (
    <Main className="dol-question drag-out-answer" {...rest}>
      <RelativeContainer>
        <Img
          loading="lazy"
          alt="Drag out result image"
          src={image.url}
          style={{ width: image.width, height: image.height }}
        />
        {spots.map(({ x, y, id }, spotIdx) => {
          return (
            <Spot style={{ left: x, top: y }} key={id}>
              <Answer
                className={cl({
                  default: spotIdx !== userAnswers[index],
                  correct: spotIdx === correctOptions[index].key,
                  'in-correct':
                    spotIdx !== correctOptions[index].key && userAnswers[index] === spotIdx,
                })}
              >
                {numberToABC(spotIdx + 1)}
              </Answer>
            </Spot>
          );
        })}
      </RelativeContainer>

      <Results>
        {results.map(
          ({
            number,
            id,
            userAnswer,
            markedType,
            correctText,
            correctAnswer,
            isWatching,
            goHere,
          }) =>
            isWatching ? (
              <DragOutMobileResult
                onClick={goHere}
                key={id}
                number={number}
                type={markedType}
                correctAnswer={correctAnswer}
                correctText={correctText}
                userAnswer={userAnswer}
                testMode={store.testMode}
              />
            ) : (
              <DragOutMobile
                onClick={goHere}
                number={number}
                className={cl('no-focus', { empty: userAnswer === undefined })}
                answer={userAnswer}
                text={correctText}
                key={id}
              />
            )
        )}
      </Results>
    </Main>
  );
});

/**
 * purpose: for inner element which has absolute poisition -> not affected by padding/margin
 */
const RelativeContainer = styled.div`
  position: relative;
  margin: 0 auto;
`;
